// src/engine/WasmChessEngine.ts
// TypeScript wrapper for the Rust/WASM chess engine
// Uses fetch to load WASM from public folder

import { Board, Piece, Position, PieceColor } from "../types/Board";

// Types for WASM module
interface WasmMoveData {
  from_row: number;
  from_col: number;
  to_row: number;
  to_col: number;
  promotion?: number;
}

interface WasmValidationData {
  is_valid: boolean;
  reason?: string;
  is_castling: boolean;
  is_en_passant: boolean;
  is_promotion: boolean;
  captured_piece_type?: number;
}

interface WasmPieceData {
  piece_type: number;
  piece_name: string;
  color: number;
  has_moved: boolean;
}

// Piece type mapping: TypeScript name -> Rust enum value
const PIECE_TYPE_TO_RUST: Record<string, number> = {
  empress: 0,
  chancellor: 1,
  emperor: 2,
  queen: 3,
  duke: 4,
  king: 5,
  fortress: 6,
  citadel: 7,
  cathedral: 8,
  castle: 9,
  rook: 9, // Alias
  armory: 10,
  abbey: 11,
  pope: 12,
  archbishop: 13,
  cardinal: 14,
  bishop: 15,
  priest: 16,
  nun: 17,
  marshall: 18,
  captain: 19,
  sergeant: 20,
  guard: 21,
  archer: 22,
  knight: 23,
  catapult: 24,
  ram: 25,
  mace: 26,
  lance: 27,
  hatchet: 28,
  sword: 29,
  angel: 30,
  saint: 31,
  wizard: 32,
  squire: 33,
  knave: 34,
  pawn: 35,
};

// Reverse mapping: Rust enum value -> TypeScript name
const RUST_TO_PIECE_TYPE: Record<number, string> = Object.fromEntries(
  Object.entries(PIECE_TYPE_TO_RUST).map(([k, v]) => [v, k])
);

// WASM module interface
let wasmModule: any = null;
let ChessEngineClass: any = null;
let wasmInitialized = false;

/**
 * Initialize the WASM module by loading from public/wasm folder
 * Call this once at app startup
 */
export async function initWasmEngine(): Promise<boolean> {
  if (wasmInitialized && wasmModule) {
    return true;
  }

  try {
    const publicUrl = process.env.PUBLIC_URL || "";
    const jsUrl = `${publicUrl}/wasm/expanded_chess_engine.js`;
    const wasmUrl = `${publicUrl}/wasm/expanded_chess_engine_bg.wasm`;

    console.log(`[WASM] Loading JS from: ${jsUrl}`);
    console.log(`[WASM] Loading WASM from: ${wasmUrl}`);

    // Fetch and execute the JS glue code
    const jsResponse = await fetch(jsUrl);
    if (!jsResponse.ok) {
      throw new Error(
        `Failed to fetch JS: ${jsResponse.status} ${jsResponse.statusText}`
      );
    }
    const jsCode = await jsResponse.text();

    // Create a blob URL to import the module
    const blob = new Blob([jsCode], { type: "application/javascript" });
    const blobUrl = URL.createObjectURL(blob);

    // Import the module
    const wasm = await import(/* webpackIgnore: true */ blobUrl);

    // Clean up blob URL
    URL.revokeObjectURL(blobUrl);

    // Fetch the WASM binary
    const wasmResponse = await fetch(wasmUrl);
    if (!wasmResponse.ok) {
      throw new Error(
        `Failed to fetch WASM: ${wasmResponse.status} ${wasmResponse.statusText}`
      );
    }
    const wasmBytes = await wasmResponse.arrayBuffer();

    // Initialize with the WASM bytes
    if (typeof wasm.default === "function") {
      await wasm.default(wasmBytes);
    } else if (typeof wasm.init === "function") {
      await wasm.init(wasmBytes);
    } else {
      // Try instantiating directly
      const wasmInstance = await WebAssembly.instantiate(
        wasmBytes,
        wasm.imports || {}
      );
      if (wasm.__wbg_set_wasm) {
        wasm.__wbg_set_wasm(wasmInstance.instance.exports);
      }
    }

    wasmModule = wasm;
    ChessEngineClass = wasm.ChessEngine;
    wasmInitialized = true;

    console.log(`[WASM] ✅ Chess engine loaded successfully!`);

    // Try to get version info if available
    if (typeof wasm.get_version === "function") {
      console.log(`[WASM] Version: ${wasm.get_version()}`);
    }
    if (typeof wasm.get_piece_type_count === "function") {
      console.log(
        `[WASM] Supported piece types: ${wasm.get_piece_type_count()}`
      );
    }

    return true;
  } catch (error) {
    console.error("[WASM] Failed to load chess engine:", error);
    console.log("[WASM] Falling back to TypeScript engine");
    wasmInitialized = false;
    return false;
  }
}

/**
 * Check if WASM engine is available
 */
export function isWasmAvailable(): boolean {
  return wasmInitialized && wasmModule !== null && ChessEngineClass !== null;
}

/**
 * WASM-accelerated chess engine wrapper
 */
export class WasmChessEngine {
  private engine: any;
  private rows: number;
  private cols: number;

  constructor(rows: number, cols: number) {
    if (!isWasmAvailable()) {
      throw new Error(
        "WASM engine not initialized. Call initWasmEngine() first."
      );
    }

    this.rows = rows;
    this.cols = cols;
    this.engine = new ChessEngineClass(rows, cols);
  }

  /**
   * Load board state from TypeScript Board structure
   */
  loadBoard(board: Board, sideToMove: PieceColor = "white"): void {
    this.engine.clear_board();

    for (let row = 0; row < board.dimensions.rows; row++) {
      for (let col = 0; col < board.dimensions.cols; col++) {
        const piece = board.squares[row][col];
        if (piece) {
          const pieceType = PIECE_TYPE_TO_RUST[piece.type.toLowerCase()];
          if (pieceType !== undefined) {
            const color = piece.color === "white" ? 0 : 1;
            this.engine.set_piece(
              row,
              col,
              pieceType,
              color,
              piece.hasMoved || false
            );
          }
        }
      }
    }

    this.engine.set_side_to_move(sideToMove === "white" ? 0 : 1);
  }

  /**
   * Load board from a Map (PlayOnline format)
   */
  loadBoardFromMap(
    piecesMap: Map<string, Piece>,
    rows: number,
    cols: number,
    sideToMove: PieceColor = "white"
  ): void {
    this.engine.clear_board();

    piecesMap.forEach((piece, key) => {
      let parts = key.split("-");
      if (parts.length !== 2) {
        parts = key.split(",");
      }

      if (parts.length === 2) {
        const row = parseInt(parts[0], 10);
        const col = parseInt(parts[1], 10);

        if (!isNaN(row) && !isNaN(col)) {
          const pieceType = PIECE_TYPE_TO_RUST[piece.type.toLowerCase()];
          if (pieceType !== undefined) {
            const color = piece.color === "white" ? 0 : 1;
            this.engine.set_piece(
              row,
              col,
              pieceType,
              color,
              piece.hasMoved || false
            );
          }
        }
      }
    });

    this.engine.set_side_to_move(sideToMove === "white" ? 0 : 1);
  }

  /**
   * Get all legal moves for the current side to move
   */
  getLegalMoves(): Position[] {
    const movesJson = this.engine.get_legal_moves();
    const moves: WasmMoveData[] = JSON.parse(movesJson);

    return moves.map((m) => ({ row: m.to_row, col: m.to_col }));
  }

  /**
   * Get all legal moves with full move information
   */
  getLegalMovesDetailed(): Array<{
    from: Position;
    to: Position;
    promotion?: string;
  }> {
    const movesJson = this.engine.get_legal_moves();
    const moves: WasmMoveData[] = JSON.parse(movesJson);

    return moves.map((m) => ({
      from: { row: m.from_row, col: m.from_col },
      to: { row: m.to_row, col: m.to_col },
      promotion:
        m.promotion !== undefined ? RUST_TO_PIECE_TYPE[m.promotion] : undefined,
    }));
  }

  /**
   * Get legal moves for a specific piece
   */
  getLegalMovesForPiece(row: number, col: number): Position[] {
    const movesJson = this.engine.get_legal_moves_for_piece(row, col);
    const moves: WasmMoveData[] = JSON.parse(movesJson);

    return moves.map((m) => ({ row: m.to_row, col: m.to_col }));
  }

  /**
   * Validate a move
   */
  validateMove(
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    promotion?: string
  ): WasmValidationData {
    const promoType = promotion
      ? PIECE_TYPE_TO_RUST[promotion.toLowerCase()]
      : undefined;
    const resultJson = this.engine.validate_move(
      fromRow,
      fromCol,
      toRow,
      toCol,
      promoType
    );
    return JSON.parse(resultJson);
  }

  /**
   * Make a move on the internal board
   */
  makeMove(
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    promotion?: string
  ): void {
    const promoType = promotion
      ? PIECE_TYPE_TO_RUST[promotion.toLowerCase()]
      : undefined;
    this.engine.make_move(fromRow, fromCol, toRow, toCol, promoType);
  }

  /**
   * Check if current side is in check
   */
  isInCheck(): boolean {
    return this.engine.is_in_check();
  }

  /**
   * Check if current side is in checkmate
   */
  isCheckmate(): boolean {
    return this.engine.is_checkmate();
  }

  /**
   * Check if current side is in stalemate
   */
  isStalemate(): boolean {
    return this.engine.is_stalemate();
  }

  /**
   * Get game status as string
   */
  getGameStatus(): string {
    return this.engine.get_game_status();
  }

  /**
   * Get current side to move
   */
  getSideToMove(): PieceColor {
    return this.engine.get_side_to_move() === 0 ? "white" : "black";
  }

  /**
   * Set side to move
   */
  setSideToMove(color: PieceColor): void {
    this.engine.set_side_to_move(color === "white" ? 0 : 1);
  }

  /**
   * Count legal moves for current side
   */
  countLegalMoves(): number {
    return this.engine.count_legal_moves();
  }

  /**
   * Get piece at position
   */
  getPiece(row: number, col: number): Piece | null {
    const pieceJson = this.engine.get_piece(row, col);
    if (pieceJson === "null") return null;

    const data: WasmPieceData = JSON.parse(pieceJson);
    return {
      type: data.piece_name.toLowerCase(),
      color: data.color === 0 ? "white" : "black",
      hasMoved: data.has_moved,
      position: { row, col },
      rules: { movements: [] }, // Rules are handled by WASM engine
    } as unknown as Piece;
  }

  /**
   * Free WASM memory (call when done with this engine instance)
   */
  dispose(): void {
    if (this.engine && this.engine.free) {
      this.engine.free();
    }
  }
}

/**
 * Hybrid move validator that uses WASM when available, falls back to TypeScript
 */
export class HybridMoveValidator {
  private wasmEngine: WasmChessEngine | null = null;

  /**
   * Get valid moves using WASM acceleration
   */
  getValidMoves(
    position: Position,
    piecesMap: Map<string, Piece>,
    rows: number,
    cols: number,
    currentTurn: PieceColor
  ): Position[] {
    if (!isWasmAvailable()) {
      console.warn("[WASM] Not available, cannot get moves");
      return [];
    }

    try {
      if (
        !this.wasmEngine ||
        this.wasmEngine["rows"] !== rows ||
        this.wasmEngine["cols"] !== cols
      ) {
        this.wasmEngine?.dispose();
        this.wasmEngine = new WasmChessEngine(rows, cols);
      }

      this.wasmEngine.loadBoardFromMap(piecesMap, rows, cols, currentTurn);
      return this.wasmEngine.getLegalMovesForPiece(position.row, position.col);
    } catch (error) {
      console.error("[WASM] Error getting valid moves:", error);
      return [];
    }
  }

  /**
   * Check if king is in check using WASM
   */
  isKingInCheck(
    piecesMap: Map<string, Piece>,
    color: PieceColor,
    rows: number,
    cols: number
  ): boolean {
    if (!isWasmAvailable()) {
      return false;
    }

    try {
      if (
        !this.wasmEngine ||
        this.wasmEngine["rows"] !== rows ||
        this.wasmEngine["cols"] !== cols
      ) {
        this.wasmEngine?.dispose();
        this.wasmEngine = new WasmChessEngine(rows, cols);
      }

      this.wasmEngine.loadBoardFromMap(piecesMap, rows, cols, color);
      return this.wasmEngine.isInCheck();
    } catch (error) {
      console.error("[WASM] Error checking check:", error);
      return false;
    }
  }

  dispose(): void {
    this.wasmEngine?.dispose();
    this.wasmEngine = null;
  }
}
